import { FactCheckOutlined, GroupOutlined, SearchOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { ALLOWED_GOOGLE_DOMAINS } from '../services/authWhitelistService';
import { IMG } from '../theme/assets';
import type { AccountLinkRequired } from '../types';

/** ブランドパネルの訴求ポイント（mock 案B 準拠）。 */
const FEATURES = [
  { icon: FactCheckOutlined, label: '4分類で判定（正確・不正確・部分的・検証不可）' },
  { icon: SearchOutlined, label: '出典つきで根拠を提示' },
  { icon: GroupOutlined, label: 'グループで検証を共有' },
] as const;

const Login: React.FC = () => {
  const { signInWithGoogle, signInWithMicrosoft, linkPendingCredential } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'microsoft' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 同じアドレスが別のログイン手段で登録済みだった場合、連携の案内を出すために保持する
  const [linkRequest, setLinkRequest] = useState<AccountLinkRequired | null>(null);

  // リダイレクト先を決定（ログイン前にアクセスしようとしたページがあればそこへ）
  const from =
    (location.state as any)?.redirectTo || (location.state as any)?.from?.pathname || '/';

  const handleError = (error: any) => {
    // 別のログイン手段で登録済みの場合は、エラーではなく連携の案内を出す
    if (error?.code === 'auth/account-link-required') {
      setLinkRequest(error as AccountLinkRequired);
      setError(null);
      return;
    }

    console.error('Login Error:', error);

    // エラーメッセージを日本語化
    let errorMessage = 'ログインに失敗しました。';
    if (error.code === 'auth/domain-not-allowed') {
      // ドメイン制限エラー（カスタムエラー）
      errorMessage = error.message;
    } else if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'ログインがキャンセルされました。';
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = 'ポップアップがブロックされました。ブラウザの設定を確認してください。';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'ネットワークエラーが発生しました。';
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      errorMessage = '同じメールアドレスで別の認証方法が登録されています。';
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = 'ログインがキャンセルされました。';
    }

    setError(errorMessage);
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoadingProvider('google');
      setError(null);
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      setLoadingProvider('microsoft');
      setError(null);
      await signInWithMicrosoft();
      navigate(from, { replace: true });
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleLink = async () => {
    if (!linkRequest) return;
    try {
      setLoadingProvider(linkRequest.existingProvider);
      setError(null);
      await linkPendingCredential(linkRequest);
      setLinkRequest(null);
      navigate(from, { replace: true });
    } catch (error: any) {
      setLinkRequest(null);
      handleError(error);
    } finally {
      setLoadingProvider(null);
    }
  };

  const isLoading = loadingProvider !== null;

  // 各ボタンの下に添える、利用できるアドレスの案内
  const hintSx = {
    fontSize: 11.5,
    color: tokens.faint,
    textAlign: 'center' as const,
    mt: 0.75,
    lineHeight: 1.5,
  };

  // 右フォーム面の背景（ライトは淡グラデ／ダークは沈んだ面）
  const formBg =
    themeMode === 'dark'
      ? 'linear-gradient(180deg, #14161d, #0f1116)'
      : 'linear-gradient(180deg, #fbfcff, #f4f6fb)';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        minHeight: 'var(--app-vh)',
      }}
    >
      {/* 左：ブランド */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          color: '#fff',
          px: { xs: 3.5, md: 6, lg: 13 },
          py: { xs: 4.5, md: 6 },
          background: 'linear-gradient(150deg, #667eea 0%, #7c5cff 55%, #0d9488 130%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(600px 320px at 90% 110%, rgba(255,255,255,.18), transparent 60%), radial-gradient(500px 300px at 0% 0%, rgba(255,255,255,.12), transparent 55%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 600 }}>
          <Box
            component="img"
            src={IMG.owl.hero}
            alt=""
            sx={{
              width: { xs: 96, md: 196 },
              height: { xs: 96, md: 196 },
              objectFit: 'contain',
              filter: 'drop-shadow(0 12px 22px rgba(20,20,50,.35))',
              mb: 1.75,
            }}
          />
          <Box>
            <Box
              component="img"
              src="/mikiwame-logo.svg"
              alt="MIKIWAME"
              sx={{
                height: 80,
                mb: 3.5,
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.3))',
              }}
            />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 28, md: 45 },
              fontWeight: 900,
              lineHeight: 1.3,
              m: 0,
              mb: 2.5,
              color: '#fff',
              textShadow: '0 2px 16px rgba(0,0,0,0.25)',
            }}
          >
            その主張、
            <br />
            根拠から見極める。
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 15, md: 18 },
              lineHeight: 1.75,
              color: 'rgba(255,255,255,.92)',
              m: 0,
              mb: 4.25,
              maxWidth: 540,
            }}
          >
            PDF・動画・指定ドメインの主張をAIが抽出し、信頼できる情報源と照らし合わせて根拠つきで検証します。
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.1 }}>
            {FEATURES.map(({ icon: Icon, label }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  fontSize: 17.5,
                  fontWeight: 700,
                }}
              >
                <Box
                  sx={{
                    width: 43,
                    height: 43,
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,.18)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 24, color: '#fff' }} />
                </Box>
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* 右：フォーム */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3.5, md: 4 },
          py: 5,
          background: formBg,
        }}
      >
        <Box sx={{ width: 'min(480px, 100%)' }}>
          <Typography
            component="h2"
            sx={{ fontSize: 31, fontWeight: 900, m: 0, textAlign: 'center', color: tokens.ink }}
          >
            ログイン
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              color: tokens.muted,
              textAlign: 'center',
              mt: 1.5,
              mb: 4.25,
              lineHeight: 1.55,
            }}
          >
            アカウントを選択してログインしてください
          </Typography>

          {/* エラー表示 */}
          {error && (
            <Box
              role="alert"
              sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: 'flex-start',
                background: tokens.verdict.inaccurate.tint,
                border: `1px solid ${themeMode === 'dark' ? 'rgba(248,113,113,0.35)' : '#f5c9c9'}`,
                color: tokens.verdict.inaccurate.color,
                borderRadius: '13px',
                px: 1.875,
                py: 1.625,
                fontSize: 13,
                lineHeight: 1.5,
                mb: 2.25,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                sx={{ width: 18, height: 18, flexShrink: 0, mt: '1px' }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v5M12 16h.01" />
              </Box>
              <span>{error}</span>
            </Box>
          )}

          {/* 別のログイン手段で登録済みだった場合の連携案内 */}
          {linkRequest && (
            <Box
              sx={{
                background: themeMode === 'dark' ? 'rgba(96,165,250,0.12)' : '#eef4ff',
                border: `1px solid ${themeMode === 'dark' ? 'rgba(96,165,250,0.35)' : '#c7d8f7'}`,
                borderRadius: '13px',
                px: 1.875,
                py: 1.75,
                mb: 2.25,
              }}
            >
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: tokens.ink, mb: 0.75 }}>
                アカウントの連携が必要です
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.muted, lineHeight: 1.6, mb: 1.5 }}>
                {linkRequest.email ? `${linkRequest.email} は` : 'このメールアドレスは'}既に
                {linkRequest.existingProvider === 'google' ? 'Google' : 'Microsoft'}
                アカウントで登録されています。本人確認のため一度
                {linkRequest.existingProvider === 'google' ? 'Google' : 'Microsoft'}
                でログインしてください。連携が済めば、次回からどちらでもログインできます。
                これまでのデータはそのまま引き継がれます。
              </Typography>
              <Button
                onClick={handleLink}
                disabled={isLoading}
                sx={{
                  width: '100%',
                  background: tokens.indigo,
                  color: '#fff',
                  borderRadius: '11px',
                  py: 1.25,
                  fontSize: 14,
                  fontWeight: 800,
                  textTransform: 'none',
                  '&:hover': { background: tokens.indigo, opacity: 0.9 },
                  '&.Mui-disabled': { background: tokens.indigo, color: '#fff', opacity: 0.6 },
                }}
              >
                {isLoading
                  ? '連携中...'
                  : `${linkRequest.existingProvider === 'google' ? 'Google' : 'Microsoft'}でログインして連携する`}
              </Button>
            </Box>
          )}

          {/* Google ログインボタン */}
          <Button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            disableRipple
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.625,
              background: '#fff',
              color: '#1f2433',
              border: `1.5px solid ${tokens.line}`,
              borderRadius: '14px',
              p: 2.25,
              fontSize: 16.5,
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: '0 10px 24px rgba(40,40,90,.1)',
              transition: '.15s',
              '&:hover': {
                background: '#fff',
                borderColor: '#c9ced9',
                boxShadow: '0 14px 30px rgba(40,40,90,.14)',
              },
              '&.Mui-disabled': {
                background: '#fff',
                color: '#1f2433',
                opacity: 0.7,
              },
            }}
          >
            {loadingProvider === 'google' ? (
              <CircularProgress size={22} sx={{ color: tokens.teal }} thickness={5} />
            ) : (
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{ width: 24, height: 24, flexShrink: 0 }}
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
                />
              </Box>
            )}
            {loadingProvider === 'google' ? 'ログイン中...' : 'Googleでログイン'}
          </Button>
          <Typography sx={hintSx}>{ALLOWED_GOOGLE_DOMAINS.join('　')} のアドレス</Typography>

          {/* Microsoft ログインボタン（Microsoft Entra ID / SSO）
              許可テナントは VITE_AZURE_TENANT_ID で固定している（config/firebase.ts）。 */}
          <Button
            onClick={handleMicrosoftSignIn}
            disabled={isLoading}
            disableRipple
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.625,
              mt: 1.75,
              background: '#fff',
              color: '#1f2433',
              border: `1.5px solid ${tokens.line}`,
              borderRadius: '14px',
              p: 2.25,
              fontSize: 16.5,
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: '0 10px 24px rgba(40,40,90,.1)',
              transition: '.15s',
              '&:hover': {
                background: '#fff',
                borderColor: '#c9ced9',
                boxShadow: '0 14px 30px rgba(40,40,90,.14)',
              },
              '&.Mui-disabled': {
                background: '#fff',
                color: '#1f2433',
                opacity: 0.7,
              },
            }}
          >
            {loadingProvider === 'microsoft' ? (
              <CircularProgress size={22} sx={{ color: tokens.teal }} thickness={5} />
            ) : (
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{ width: 24, height: 24, flexShrink: 0 }}
              >
                <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
                <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
                <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
                <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
              </Box>
            )}
            {loadingProvider === 'microsoft' ? 'ログイン中...' : 'Microsoftでログイン'}
          </Button>
          <Typography sx={hintSx}>テレビ朝日の組織アカウント</Typography>

          {/* 補足情報 */}
          <Typography
            sx={{
              fontSize: 12,
              color: tokens.faint,
              textAlign: 'center',
              mt: 3,
              lineHeight: 1.65,
              '& a': { color: tokens.indigo, textDecoration: 'none', fontWeight: 700 },
            }}
          >
            ログインすることで、
            {/* biome-ignore lint/a11y/useValidAnchor: 利用規約・ポリシーは実装予定のプレースホルダリンク */}
            <a href="#" onClick={(e) => e.preventDefault()}>
              利用規約
            </a>
            と
            {/* biome-ignore lint/a11y/useValidAnchor: 利用規約・ポリシーは実装予定のプレースホルダリンク */}
            <a href="#" onClick={(e) => e.preventDefault()}>
              プライバシーポリシー
            </a>
            に同意したものとみなします。
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
